import React, { useMemo, useRef, useState } from "react";
import { Spacer } from "../../components/_shared-components/arrangement/Space";
import {
  EnrichedSwarovskiProductFrontendProductAggregatedReviews,
  SwarovskiOptikLocaleFrontend,
  type TranslatedValue,
} from "@swo/storefront";
import { css } from "@emotion/react";
import { ContentContainer } from "../../components/_shared-components/arrangement/Container";
import { CSS_SPACER_SIZES } from "../../styles/styles-css-variables";
import { PDPHeadlineOnBackground } from "./pdp-headline";
import { PDPReviewBox } from "./PDPReviewBox";
import { PDPReviewsHistorgram } from "./PDPReviewsHistorgram";
import { PDPReviewsDetailsReviewsList } from "./PDPReviewsDetailsReviewsList";
import { ReviewTranslations } from "./reviewTranslations";
import { getReviewDateAgoLabel } from "./getReviewDateAgoLabel";
import { CSS_MQ } from "../../styles/breakpoints";
import { PDPReviewFiltersBox } from "./PDPReviewFiltersBox";
import { Translated } from "../../domain/translations/translations";
import { GeneralTranslation } from "@swo/storefront";
import { reviewsFilter } from "./reviewsFilter";
import { PDPReviewsLoadMore } from "./PDPReviewsLoadMore";
import { usePageLocaleContext } from "../../global/page-context";
import { T } from "../../components/T";

const LOAD_MORE_PAGE_SIZE = 4;
const INITIAL_PAGE_SIZE = 4;

export const PDPReviewsDetails: React.FC<{
  headline: TranslatedValue;
  data: NonNullable<EnrichedSwarovskiProductFrontendProductAggregatedReviews>;
  id: string;
  t: ReviewTranslations;
  gt: Translated<GeneralTranslation>;
}> = ({ headline, data, id, t, gt }) => {
  const [now] = useState(new Date());
  const { locale } = usePageLocaleContext();

  const [state, setState] = useState<{ starsFilter: number | null; showCountLimit: number }>({
    starsFilter: null,
    showCountLimit: INITIAL_PAGE_SIZE,
  });

  const onReviewFilteredByStars = (starValue: number) => {
    setState(s => (s.starsFilter === starValue ? { ...s, starsFilter: null } : { ...s, starsFilter: starValue }));
  };

  const reviewDateFormatter = getReviewDateAgoLabel(now, locale);

  const filteredReviews = useMemo(() => reviewsFilter(data.reviews)(state), [data.reviews, state]);

  return (
    <>
      <Spacer size={"md"} />

      <div
        css={css`
          background-color: var(--so-green-white);
          padding: ${CSS_SPACER_SIZES.md} 0px;

          --p: 32px;
          ${CSS_MQ.atMostSm} {
            --p: 24px;
          }
        `}
      >
        <ContentContainer>
          <PDPHeadlineOnBackground>
            <T>{headline}</T>
          </PDPHeadlineOnBackground>

          <div
            id={id}
            css={css`
              display: grid;

              ${CSS_MQ.atLeastMd} {
                grid-template-columns: repeat(3, minmax(0, 1fr));
              }
              ${CSS_MQ.atLeastLg} {
                grid-template-columns: repeat(4, minmax(0, 1fr));
              }
              gap: 25px;
            `}
          >
            <div
              css={css`
                ${CSS_MQ.atLeastMd} {
                  grid-column: span 1;
                }
              `}
            >
              <div
                css={css`
                  ${CSS_MQ.atMostSm} {
                    display: grid;
                    grid-gap: 16px;
                    grid-template-columns: repeat(5, minmax(0, 1fr));
                  }

                  ${CSS_MQ.atMostXs} {
                    grid-template-columns: repeat(6, minmax(0, 1fr));
                  }
                `}
              >
                <div
                  css={css`
                    ${CSS_MQ.atMostSm} {
                      grid-column: span 2;

                      display: flex;
                      align-items: center;
                      > * {
                        width: 100%;
                      }
                    }

                    ${CSS_MQ.atMostXs} {
                      grid-column: span 3;
                    }
                  `}
                >
                  <PDPReviewBox
                    locale={locale}
                    averageRating={data.averageRating}
                    reviewsCount={data.reviews.length}
                    t={t}
                  />
                </div>

                <div
                  css={css`
                    ${CSS_MQ.atMostXs} {
                      grid-column: span 3;
                    }

                    ${CSS_MQ.atMostSm} {
                      grid-column: span 3;
                      align-items: center;
                      display: flex;

                      > * {
                        flex-grow: 1;
                      }
                    }
                  `}
                >
                  <PDPReviewsHistorgram locale={locale} data={data} onClick={onReviewFilteredByStars} />
                </div>
              </div>
            </div>

            <div
              className="u-flex u-flex-col"
              css={css`
                ${CSS_MQ.atLeastMd} {
                  grid-column: span 2;
                }
                ${CSS_MQ.atLeastLg} {
                  grid-column: span 3;
                }
              `}
            >
              <PDPReviewFiltersBox
                filter={state.starsFilter}
                resetFilter={() => setState(s => ({ ...s, starsFilter: null }))}
                t={t}
              />

              <PDPReviewsDetailsReviewsList
                reviews={filteredReviews.filtered}
                reviewDateFormatter={reviewDateFormatter}
                t={t}
              />

              {filteredReviews.hasMore ? (
                <PDPReviewsLoadMore
                  onClick={() => setState(s => ({ ...s, showCountLimit: s.showCountLimit + LOAD_MORE_PAGE_SIZE }))}
                  label={gt("loadMoreLabel")}
                />
              ) : null}
            </div>
          </div>
        </ContentContainer>
      </div>
    </>
  );
};
